<template>
  <v-data-table
    class="no-border"
    :loading="loading"
    :headers="headers"
    :items="data"
    :search="search"
    :sort-by="sortArray"
    :sort-desc="sortDirectionArray"
    multi-sort
    :items-per-page="itemPerPage > 0 ? itemPerPage : data.length + 1"
    hide-default-footer
    hide-default-header
    :mobile-breakpoint="0"
    dense
  >
    <template v-slot:item="{ item }">
      <ProductSummaryDialog :id="item.product_id" :corr="item">
        <td width="180px" class="correction-td">
          <a
            style="cursor: pointer; color:rgba(0, 0, 0, 0.87);"
            class="font-capitalize caption"
            >{{ item.nom }}</a
          >

          <v-row no-gutters dense v-if="$vuetify.breakpoint.xsOnly">
            <v-chip
              small
              label
              v-if="item.correction"
              class="block-chip my-1"
              :color="getColor(item)"
            >
              <div>
                <span class="ml-1 caption">
                  {{ item.date | shortLocalFormat }}</span
                >
              </div>
              <div
                class="ml-1"
                style="min-width: 75px; display: flex; flex-grow: 1; align-items: center; justify-content: flex-end;"
              >
                <span class="caption mr-1" v-if="item.correction < 0"
                  >Manque</span
                >
                <span class="caption mr-1" v-else>Surplus</span>
                <span class="font-weight-bold">{{
                  Math.abs(item.correction)
                }}</span>
                <span v-if="item.correction" class="caption ml-1">{{
                  getUnit(item)
                }}</span>
                <span class="caption ml-2" style="width: 48px; text-align: end;"
                  >{{ item.correction_value }}$</span
                >
              </div>
            </v-chip>
          </v-row>
        </td>
        <td v-if="$vuetify.breakpoint.smAndUp">
          <v-chip
            small
            label
            v-if="item.correction"
            class="block-chip my-1"
            :color="getColor(item)"
          >
            <div>
              <span class="ml-1 caption">
                {{ item.date | shortLocalFormat }}</span
              >
            </div>
            <div
              class="ml-1"
              style="min-width: 75px; display: flex; flex-grow: 1; align-items: center; justify-content: flex-end;"
            >
              <span class="caption mr-1" v-if="item.correction < 0"
                >Manque</span
              >
              <span class="caption mr-1" v-else>Surplus</span>
              <span class="font-weight-bold">{{
                Math.abs(item.correction)
              }}</span>
              <span v-if="item.correction" class="caption ml-1">{{
                getUnit(item)
              }}</span>
              <span class="caption ml-2" style="width: 48px; text-align: end;"
                >{{ item.correction_value }}$</span
              >
            </div>
          </v-chip>
        </td>
      </ProductSummaryDialog>
    </template>
  </v-data-table>
</template>

<script>
import { api } from "@/api";
import { mapGetters } from "vuex";
import { round10 } from "round10";
import moment from "moment";
import ProductSummaryDialog from "./ProductSummaryDialog";
export default {
  name: "CorrectionProductChart",
  components: {
    ProductSummaryDialog
  },
  props: {
    itemPerPage: {
      type: Number,
      default: 0
    },
    delta: {
      type: Number,
      default: 1
    },
    search: {
      type: String,
      default: ""
    },
    sort: {
      type: String,
      default: "correction_value"
    }
  },
  mounted() {
    this.getData();
  },
  computed: {
    ...mapGetters(["getProducts"])
  },
  data() {
    return {
      data: [],
      loading: false,
      headers: [
        { value: "nom" },
        { value: "date" },
        { value: "correction_value" }
      ],
      sortArray: ["date", "correction_value"],
      sortDirectionArray: [true, true]
    };
  },
  methods: {
    async getData() {
      try {
        this.loading = true;
        const d = await api.getInventoryRecordsCorrections({
          start: moment()
            .hours(0)
            .minutes(0)
            .subtract(this.delta, "d")
            .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ"),
          end: moment().format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
        });
        this.data = d
          .filter(x => x.is_omitted === false)
          .map(x => {
            const product = this.getProducts.find(p => p.id === x.fk_product);
            if (product) {
              return {
                ...x,
                product_id: product.id,
                nom: product.nom,
                unit: product.data ? product.data.unite_mesure_inventory : "",
                date: moment(moment(x.created).format("YYYY-MM-DD")).toDate(),
                correction: round10(Number(x.quantity), -2),
                computedQuantity: null,
                correction_value: Math.abs(
                  round10(
                    Number(x.quantity) * Number(product.data.prix_inventory),
                    -2
                  )
                )
              };
            } else {
              return null;
            }
          })
          .filter(
            x =>
              x !== null &&
              x.correction !== undefined &&
              x.correction !== null &&
              x.correction !== 0
          );
      } finally {
        this.loading = false;
      }
    },
    goToDetails(id) {
      this.$router.push({ name: "StatisticsDetails", params: { id: id } });
    },
    getColor(item) {
      let c = "rgba(0,0,0, 0.02)";
      if (item.correction_value_mean < 0) {
        c = `rgba(244, 67, 54, ${this.getOpacityValue(
          item.correction_value_mean
        )})`;
      } else if (item.correction_value_mean > 0) {
        c = `rgba(76, 175, 80, ${this.getOpacityValue(
          item.correction_value_mean
        )})`;
      }
      return c;
    },
    getOpacityValue(value) {
      let v = Math.abs(value) + (4 * Math.abs(value)) / 5;
      return v <= 1 ? v : 1;
    },
    getUnit(item) {
      if (item.unit && item.unit !== null) return item.unit.replace(/ .*/, "");
      return "";
    }
  },
  watch: {
    getProducts(oV, nV) {
      if (oV.length !== nV.length) {
        this.getData();
      }
    },
    delta() {
      this.getData();
    },
    sort(v) {
      this.sortArray = ["date", v];
      if (v === "correction_value") this.sortDirectionArray = [true, true];
      if (v === "correction_value_mean")
        this.sortDirectionArray = [true, false];
    }
  }
};
</script>

<style scoped lang="scss">
.correction-td {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
